import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import TrianglesMiniSvg from '../other/svg/TrianglesMiniSvg'
import XMarkSvg from '../other/svg/XMarkSvg'
import { fieldBoxShadow } from './FormTextInput'

export interface FormSelectOption {
  value: string
  label: string
}

interface FormSelectProps {
  value: string
  onChange: (value: string) => void
  options: FormSelectOption[]
  placeholder: string
  error?: boolean
  clearable?: boolean
}

interface MenuPosition {
  top: number
  left: number
  width: number
}

const menuShadow =
  '0px 8px 16px 0px rgba(0,0,0,0.08), 0px 4px 8px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'

const FormSelect = ({
  value,
  onChange,
  options,
  placeholder,
  error = false,
  clearable = true
}: FormSelectProps): JSX.Element => {
  const isPlaceholder = value.length === 0
  const selectedOption = options.find((option) => option.value === value)
  const [isOpen, setIsOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null)
  const controlRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const updatePosition = (): void => {
      if (controlRef.current === null) {
        return
      }
      const rect = controlRef.current.getBoundingClientRect()
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      })
    }

    if (isOpen) {
      updatePosition()
      window.addEventListener('resize', updatePosition)
      window.addEventListener('scroll', updatePosition, true)
    } else {
      setMenuPosition(null)
    }

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const selectedLabel = selectedOption?.label ?? placeholder

  const toggleOpen = (): void => {
    setIsOpen((open) => !open)
  }

  const clearValue = (event: React.MouseEvent): void => {
    event.preventDefault()
    event.stopPropagation()
    onChange('')
    setIsOpen(false)
  }

  return (
    <div className="self-stretch relative">
      <div
        ref={controlRef}
        className="self-stretch w-full h-11 pl-4 pr-1 bg-backgrounds-bg-field-component rounded-md inline-flex justify-center items-center overflow-hidden"
        style={{ boxShadow: fieldBoxShadow(error) }}>
        <button
          type="button"
          onClick={toggleOpen}
          className={`flex-1 h-full text-left text-sm font-Montserrat leading-5 truncate bg-transparent ${
            isPlaceholder ? 'text-foregrounds-fg-muted font-medium' : 'text-foregrounds-fg-base font-semibold'
          }`}>
          {selectedLabel}
        </button>
        {clearable && !isPlaceholder && (
          <button
            type="button"
            onClick={clearValue}
            className="size-8 flex justify-center items-center shrink-0"
            aria-label="Clear">
            <XMarkSvg className="size-[15px] text-buttons-text-secondary" />
          </button>
        )}
        <button type="button" onClick={toggleOpen} className="size-8 flex justify-center items-center shrink-0">
          <TrianglesMiniSvg className="size-[15px] text-foregrounds-fg-muted" />
        </button>
      </div>
      {isOpen &&
        menuPosition !== null &&
        createPortal(
          <>
            <div
              role="presentation"
              className="fixed inset-0 z-[100]"
              onClick={() => {
                setIsOpen(false)
              }}
            />
            <div
              className="fixed z-[200] p-1 bg-backgrounds-bg-field-component rounded-lg inline-flex flex-col justify-start items-start overflow-hidden"
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
                width: menuPosition.width,
                boxShadow: menuShadow
              }}>
              <div className="self-stretch max-h-52 overflow-y-auto flex flex-col justify-start items-start">
                {options.map((option) => {
                  const isSelected = option.value === value
                  return (
                    <button
                      type="button"
                      key={option.value}
                      onClick={() => {
                        onChange(option.value)
                        setIsOpen(false)
                      }}
                      className="self-stretch relative pl-9 pr-3 py-2 rounded-sm inline-flex justify-start items-center gap-2 bg-backgrounds-bg-field-component hover:bg-backgrounds-bg-component-hover">
                      {isSelected && (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="absolute left-3 size-3.5 text-foregrounds-fg-base">
                          <path
                            d="M3 7.2L5.7 10L11 4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                      <div className="flex-1 text-left text-foregrounds-fg-base text-xs font-medium font-Montserrat leading-5">
                        {option.label}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  )
}

export default FormSelect
